import { apiSlice } from '../apiSlice';
import { SuccessResponse, Paginated } from '../DTO/common.interface';
import {
	GalleryFilter,
	GameColorGallerySeriesDTO,
	GameColorGallerySeriesDTORequest,
	GameColorGalleryTreasureDTO,
	GameColorGalleryTreasureLocationDTO,
	PublishDTO,
	TreasureFilter,
} from '../DTO/treasury/gallery.interface';

export const galleryApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getGalleryPage: builder.query<
			SuccessResponse<Paginated<GameColorGallerySeriesDTO>>,
			GalleryFilter
		>({
			query: (body) => ({
				url: `/web/game/color-gallery/series/page`,
				method: 'POST',
				body,
			}),
			providesTags: ['GalleryList'],
		}),
		getGallerySortList: builder.query<
			SuccessResponse<GameColorGallerySeriesDTO[]>,
			null
		>({
			query: () => ({
				url: `/web/game/color-gallery/series/sort`,
				method: 'GET',
			}),
		}),
		sortGallery: builder.mutation<SuccessResponse<any>, string[]>({
			query: (body) => ({
				url: `/web/game/color-gallery/series/sort`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['GalleryList'],
		}),
		createGallery: builder.mutation<
			SuccessResponse<any>,
			GameColorGallerySeriesDTORequest
		>({
			query: (body) => ({
				url: `/web/game/color-gallery`,
				method: 'POST',
				body,
			}),
		}),
		updateGallery: builder.mutation<
			SuccessResponse<any>,
			GameColorGallerySeriesDTORequest
		>({
			query: (body) => ({
				url: `/web/game/color-gallery/series/${body.id}`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['GalleryDetail'],
		}),
		setGalleryStatus: builder.mutation<
			SuccessResponse<any>,
			{ publishList: PublishDTO[] }
		>({
			query: (body) => ({
				url: `/web/game/color-gallery/series/published`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['GalleryList', 'GalleryDetail'],
		}),
		getGalleryDetail: builder.query<
			SuccessResponse<GameColorGallerySeriesDTO>,
			string
		>({
			query: (id) => ({
				url: `/web/game/color-gallery/series/${id}`,
				method: 'GET',
			}),
			providesTags: ['GalleryDetail'],
		}),
		getGalleryTreasurePage: builder.query<
			SuccessResponse<Paginated<GameColorGalleryTreasureDTO>>,
			TreasureFilter
		>({
			query: (body) => ({
				url: `/web/game/color-gallery/series/${body.seriesId}/page`,
				method: 'POST',
				body,
			}),
			providesTags: ['GalleryTreasureList'],
		}),
		getGalleryTreasureSortList: builder.query<
			SuccessResponse<GameColorGalleryTreasureDTO[]>,
			string
		>({
			query: (id) => ({
				url: `/web/game/color-gallery/series/${id}/sort`,
				method: 'GET',
			}),
		}),
		sortGalleryTreasure: builder.mutation<SuccessResponse<any>, string[]>({
			query: (body) => ({
				url: `/web/game/color-gallery/treasure/sort`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['GalleryTreasureList'],
		}),
		setGalleryTreasureStatus: builder.mutation<
			SuccessResponse<any>,
			{ publishList: PublishDTO[]; id: string }
		>({
			query: (body) => ({
				url: `/web/game/color-gallery/treasure/published`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['GalleryTreasureList', 'GalleryTreasureDetail'],
		}),
		deleteGallery: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/game/color-gallery/series/${id}`,
				method: 'DELETE',
			}),
		}),
		getGalleryTreasureDetail: builder.query<
			SuccessResponse<GameColorGalleryTreasureDTO>,
			string
		>({
			query: (id) => ({
				url: `/web/game/color-gallery/treasure/${id}`,
				method: 'GET',
			}),
			providesTags: ['GalleryTreasureDetail'],
		}),
		updateGalleryTreasureDetail: builder.mutation<
			SuccessResponse<any>,
			GameColorGalleryTreasureDTO
		>({
			query: (body) => ({
				url: `/web/game/color-gallery/treasure/${body.id}`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['GalleryTreasureDetail'],
		}),
		createGalleryTreasure: builder.mutation<
			SuccessResponse<any>,
			GameColorGalleryTreasureDTO
		>({
			query: (body) => ({
				url: `/web/game/color-gallery/series/${body.galleryId}`,
				method: 'POST',
				body,
			}),
		}),
		deleteGalleryTreasure: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/game/color-gallery/treasure/${id}`,
				method: 'DELETE',
			}),
		}),
		getGalleryTreasureLocationPage: builder.query<
			SuccessResponse<Paginated<GameColorGalleryTreasureLocationDTO>>,
			string
		>({
			query: (galleryId) => ({
				url: `/web/game/color-gallery/series/${galleryId}/layer-sort`,
				method: 'GET',
			}),
		}),
		sortGalleryTreasureLocation: builder.mutation<
			SuccessResponse<any>,
			string[]
		>({
			query: (body) => ({
				url: `/web/game/color-gallery/treasure/layer-sort`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['GalleryTreasureList'],
		}),
	}),
});

export const {
	useGetGalleryPageQuery,
	useGetGallerySortListQuery,
	useSortGalleryMutation,
	useCreateGalleryMutation,
	useUpdateGalleryMutation,
	useSetGalleryStatusMutation,
	useGetGalleryDetailQuery,
	useGetGalleryTreasurePageQuery,
	useGetGalleryTreasureSortListQuery,
	useSortGalleryTreasureMutation,
	useSetGalleryTreasureStatusMutation,
	useDeleteGalleryMutation,
	useGetGalleryTreasureDetailQuery,
	useUpdateGalleryTreasureDetailMutation,
	useCreateGalleryTreasureMutation,
	useDeleteGalleryTreasureMutation,
	useGetGalleryTreasureLocationPageQuery,
	useSortGalleryTreasureLocationMutation,
} = galleryApiSlice;
